import { useState } from "react";
import clsx from "clsx";
import {
  AlertTriangle,
  BadgeCheck,
  Calculator,
  FileSearch,
  Info,
  Loader2,
  ShieldQuestion,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type {
  AiAnalysis,
  AiConfidence,
  AiSource,
  AiSourcedStatement,
} from "../../api/aiApi";
import { uiLocale } from "../../utils/date/uiLocale";

/**
 * The shared AI visual language: a sparkle mark, an "AI" chip, confidence
 * labels that never read as certainty, and sources a person can check.
 */

/** The wordmark's aspect ratio, from the asset itself (720 x 190). */
const WORDMARK_RATIO = 720 / 190;

/**
 * The pactline.ai wordmark, used where the product's name would otherwise be
 * spelled out — the AI tabs, the AI chips, the assistant's header and its
 * launcher.
 *
 * Drawn as a mask filled with the current text colour rather than as a flat
 * image, so it takes the colour of whatever it sits in: white on the active
 * tab and the accent header, the accent colour on a light chip, and the right
 * tone in every theme without a second asset.
 */
export function AiWordmark({
  height = 16,
  className,
}: {
  height?: number;
  className?: string;
}) {
  const mask = {
    height,
    width: Math.round(height * WORDMARK_RATIO),
    WebkitMaskImage: "url(/pactline-ai.png)",
    maskImage: "url(/pactline-ai.png)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  } as const;
  return (
    <span
      role="img"
      aria-label="pactline.ai"
      style={mask}
      className={clsx(
        "inline-block shrink-0 bg-current align-middle",
        className,
      )}
    />
  );
}

/**
 * The short form — ".ai" — for avatars and other small round slots, where the
 * full wordmark would be too small to read. Takes its colour from the text
 * around it, as the wordmark does.
 */
export function AiGlyph({
  size = 18,
  className,
}: {
  size?: number;
  className?: string;
}) {
  const mask = {
    height: size,
    width: size,
    WebkitMaskImage: "url(/pactline-ai-short.png)",
    maskImage: "url(/pactline-ai-short.png)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  } as const;
  return (
    <span
      aria-hidden
      style={mask}
      className={clsx(
        "inline-block shrink-0 bg-current align-middle",
        className,
      )}
    />
  );
}

/**
 * How a rules-based surface says so: the Insights card and the obligation's
 * risk checks are computed from the records themselves, with no model
 * involved, and say it in their own words (the "basis" the service returns).
 * AI-generated surfaces carry the pactline.ai chip and AiNotice instead.
 */
export function RulesBasis({ basis }: { basis?: string | null }) {
  const { t } = useTranslation("ai");
  if (!basis) return null;
  return (
    <p className="flex items-start gap-1.5 text-[11px] text-[rgb(var(--text-muted))]">
      <Calculator size={12} aria-hidden className="mt-0.5 shrink-0" />
      <span>
        <span className="font-semibold uppercase tracking-wide">
          {t("rulesBased", { defaultValue: "Rules-based" })}
        </span>{" "}
        · {basis}
      </span>
    </p>
  );
}

/** Where a score sits on the red → amber → green scale. */
function scoreTone(percent: number) {
  if (percent >= 80)
    return {
      stroke: "rgb(16 185 129)",
      text: "text-emerald-700 dark:text-emerald-300",
      bar: "bg-emerald-500",
    };
  if (percent >= 40)
    return {
      stroke: "rgb(245 158 11)",
      text: "text-amber-700 dark:text-amber-300",
      bar: "bg-amber-500",
    };
  return {
    stroke: "rgb(239 68 68)",
    text: "text-red-700 dark:text-red-300",
    bar: "bg-red-500",
  };
}

/**
 * How much of what was asked for the analysis actually found, as a dial from
 * red through amber to green. It reports coverage, never a verdict — the
 * outcome is still a person's to record.
 */
export function AiScoreDial({
  found,
  total,
  label,
  size = 44,
}: {
  found: number;
  total: number;
  label?: string;
  size?: number;
}) {
  const { t } = useTranslation("ai");
  if (!total) return null;
  const percent = Math.round((found / total) * 100);
  const tone = scoreTone(percent);
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <span
      className="inline-flex items-center gap-2"
      title={t("score.hint", {
        defaultValue:
          "How much of what was looked for the analysis found. It is coverage, not a verdict.",
      })}
    >
      <svg
        width={size}
        height={size}
        role="img"
        aria-label={`${found} of ${total}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth="5"
          className="stroke-[rgb(var(--card-muted))]"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          stroke={tone.stroke}
          strokeDasharray={`${(percent / 100) * circumference} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          className={clsx("text-[11px] font-bold", tone.text)}
          fill="currentColor"
        >
          {percent}%
        </text>
      </svg>
      {label && (
        <span className="text-[11px] leading-tight text-[rgb(var(--text-muted))]">
          {label}
          <span className="block font-semibold text-[rgb(var(--text-primary))]">
            {found}/{total}
          </span>
        </span>
      )}
    </span>
  );
}

/** The same scale as a bar, where a dial would crowd the row. */
export function AiScoreBar({
  found,
  total,
  className,
}: {
  found: number;
  total: number;
  className?: string;
}) {
  if (!total) return null;
  const percent = Math.round((found / total) * 100);
  return (
    <span
      aria-hidden
      className={clsx(
        "inline-block h-1.5 w-24 overflow-hidden rounded-full bg-[rgb(var(--card-muted))]",
        className,
      )}
    >
      <span
        className={clsx("block h-full rounded-full", scoreTone(percent).bar)}
        style={{ width: `${percent}%` }}
      />
    </span>
  );
}

export function AiChip({
  label,
  className,
}: {
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation("ai");
  const text = label ?? t("chip", { defaultValue: "AI" });
  // "AI Extracted" reads as the wordmark plus what it says about the value;
  // a bare "AI" is the wordmark alone.
  const rest = text.replace(/^ai\b[\s:-]*/i, "");
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border border-[rgb(var(--text-accent))]/30 bg-[rgb(var(--text-accent))]/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-accent))]",
        className,
      )}
    >
      <Sparkles size={11} aria-hidden />
      <AiWordmark height={14} />
      {rest && <span>{rest}</span>}
    </span>
  );
}

const CONFIDENCE_STYLES: Record<AiConfidence, string> = {
  high: "border-emerald-300/60 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
  medium:
    "border-sky-300/60 bg-sky-50 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200",
  needs_review:
    "border-amber-300/60 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200",
};

export function ConfidenceBadge({ confidence }: { confidence?: AiConfidence }) {
  const { t } = useTranslation("ai");
  if (!confidence) return null;
  const label =
    confidence === "high"
      ? t("confidence.high", { defaultValue: "High confidence" })
      : confidence === "medium"
        ? t("confidence.medium", { defaultValue: "Medium confidence" })
        : t("confidence.needsReview", { defaultValue: "Needs review" });
  return (
    <span
      title={t("confidence.hint", {
        defaultValue:
          "An indication from the AI, not a certainty. Check the source.",
      })}
      className={clsx(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
        CONFIDENCE_STYLES[confidence],
      )}
    >
      {label}
    </span>
  );
}

function sourceLabel(source: AiSource, t: TFunction) {
  const parts = [
    source.section,
    source.page
      ? t("source.page", { defaultValue: "Page {{page}}", page: source.page })
      : null,
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(" · ");
  // Chunk ids say what was cited: S… contract text, E… an evidence file, R1/D1 the obligation's own record.
  const kind = (source.chunkId ?? "").charAt(0).toUpperCase();
  if (kind === "E")
    return t("source.evidence", { defaultValue: "Evidence document" });
  if (kind === "R" || kind === "D")
    return t("source.record", { defaultValue: "Obligation record" });
  return t("source.document", { defaultValue: "Contract document" });
}

/**
 * The sources behind a statement. Only verified quotes are offered as
 * sources; anything else is shown as "Source could not be confidently
 * identified" rather than as a citation.
 */
export function SourceList({
  statement,
  onOpenSource,
  compact,
}: {
  statement: Pick<AiSourcedStatement, "sources" | "sourceNotice">;
  onOpenSource?: (source: AiSource) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation("ai");
  const [showQuotes, setShowQuotes] = useState(false);
  // One chip per place cited: the model often quotes the same record or
  // clause several times for one statement, and a row of identical chips
  // tells the reader nothing more. Every quote is still listed below.
  const cited = (statement.sources ?? []).filter((s) => s.verified);
  const verified = cited.filter(
    (source, i) =>
      cited.findIndex((other) => sourceLabel(other, t) === sourceLabel(source, t)) === i,
  );

  if (verified.length === 0) {
    if (!statement.sourceNotice) return null;
    return (
      <p className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
        <ShieldQuestion size={12} aria-hidden />
        {t("source.unverified", {
          defaultValue: "Source could not be confidently identified.",
        })}
      </p>
    );
  }

  const quotes = cited.filter(
    (source, i) =>
      Boolean(source.quote) &&
      cited.findIndex((other) => other.quote === source.quote) === i,
  );

  return (
    <div className="mt-2">
      {/* One quiet row of citations rather than a stacked list: the passage
          they point at is a click away, and the quotes fold out together. */}
      <div className="flex flex-wrap items-center gap-1">
        <BadgeCheck
          size={12}
          className="shrink-0 text-emerald-700 dark:text-emerald-400"
          aria-hidden
        />
        {verified.map((source, i) => {
          const label = `${t("source.label", { defaultValue: "Source" })}: ${sourceLabel(source, t)}`;
          const chip =
            "inline-flex max-w-full items-center gap-1 rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--card-muted))]/60 px-2 py-0.5 text-[11px] text-[rgb(var(--text-secondary))]";
          if (!onOpenSource) {
            return (
              <span key={`${source.chunkId}-${i}`} className={chip}>
                <span className="truncate">{label}</span>
              </span>
            );
          }
          return (
            <button
              key={`${source.chunkId}-${i}`}
              type="button"
              onClick={() => onOpenSource(source)}
              aria-label={`${t("source.open", { defaultValue: "Open source" })}: ${sourceLabel(source, t)}`}
              className={clsx(
                chip,
                "transition-colors hover:border-[rgb(var(--text-accent))]/45 hover:bg-[rgb(var(--text-accent))]/[0.08] hover:text-[rgb(var(--text-accent))]",
              )}
            >
              <span className="truncate">{label}</span>
              <FileSearch size={11} aria-hidden className="shrink-0" />
            </button>
          );
        })}
        {!compact && quotes.length > 0 && (
          <button
            type="button"
            onClick={() => setShowQuotes((v) => !v)}
            className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-[rgb(var(--text-accent))] hover:bg-[rgb(var(--card-muted))]"
          >
            {showQuotes
              ? t("source.hideQuotes", { defaultValue: "Hide quotes" })
              : t("source.showQuotes", {
                  defaultValue: "Show quotes ({{count}})",
                  count: quotes.length,
                })}
          </button>
        )}
      </div>

      {!compact && showQuotes && (
        <ul className="mt-1.5 space-y-1">
          {quotes.map((source, i) => (
            <li key={`quote-${source.chunkId}-${i}`}>
              <blockquote className="border-l-2 border-[rgb(var(--border))] pl-2 text-[11px] italic text-[rgb(var(--text-muted))]">
                “{source.quote}”
              </blockquote>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AiNotice({
  notice,
  warnings,
}: {
  notice?: string;
  warnings?: string[];
}) {
  const { t } = useTranslation("ai");
  return (
    <div className="flex flex-col gap-1.5">
      {(warnings ?? []).map((w) => (
        <p
          key={w}
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />
          {w}
        </p>
      ))}
      <p className="flex items-start gap-2 text-[11px] text-[rgb(var(--text-muted))]">
        <Info size={12} className="mt-0.5 shrink-0" aria-hidden />
        {notice ||
          t("notice", {
            defaultValue:
              "AI-generated analysis for review. It is not legal advice; check it against the contract before relying on it.",
          })}
      </p>
    </div>
  );
}

export function AiLoading({ label }: { label: string }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-lg border border-dashed border-[rgb(var(--border))] px-3 py-3 text-xs text-[rgb(var(--text-secondary))]"
    >
      <Loader2
        size={14}
        className="animate-spin text-[rgb(var(--text-accent))]"
        aria-hidden
      />
      {label}
    </div>
  );
}

export function AiErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation("ai");
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-300/60 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
    >
      <span>{message}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md px-2 py-1 font-medium underline-offset-2 hover:underline"
        >
          {t("actions.retry", { defaultValue: "Try again" })}
        </button>
      )}
    </div>
  );
}

/** Small consistent button used across AI surfaces. */
/** The size of every AI card's own action (Generate, Extract, Find, Analyse,
 *  Refresh…), and of a picker beside one, so they line up across the tab. */
export const AI_ACTION_SIZE = "h-9 w-44 justify-center text-sm";

export function AiButton({
  children,
  onClick,
  disabled,
  variant = "secondary",
  title,
  icon: Icon,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "ghost";
  title?: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
        variant === "primary" &&
          "bg-[rgb(var(--accent-fill))] text-[rgb(var(--accent-ink))] hover:opacity-90",
        variant === "secondary" &&
          "border border-[rgb(var(--border))] bg-[rgb(var(--card))] text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--card-muted))]",
        variant === "ghost" &&
          "text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--card-muted))]",
        className,
      )}
    >
      {Icon && <Icon size={14} aria-hidden />}
      {children}
    </button>
  );
}

/** Who ran an analysis, when, and with which model and prompt version. */
export function Provenance({ analysis }: { analysis: AiAnalysis<unknown> }) {
  const { t } = useTranslation("ai");
  const when = new Date(analysis.createdAt).toLocaleString(uiLocale());
  return (
    <p className="text-[11px] text-[rgb(var(--text-muted))]">
      {t("provenance", {
        defaultValue: "Generated {{when}} by {{who}} · {{model}} · {{prompt}}",
        when,
        who: analysis.createdByName ?? "—",
        model: analysis.model ?? analysis.provider ?? "—",
        prompt: analysis.promptVersion ?? "—",
      })}
      {analysis.fromCache &&
        ` · ${t("fromCache", { defaultValue: "reused an identical earlier result" })}`}
    </p>
  );
}

/** Where a suggestion stands in human review. */
export function ReviewBadge({ status }: { status: string }) {
  const { t } = useTranslation("ai");
  const label =
    status === "accepted"
      ? t("review.accepted", { defaultValue: "Accepted" })
      : status === "edited"
        ? t("review.edited", { defaultValue: "Edited & accepted" })
        : status === "rejected"
          ? t("review.rejected", { defaultValue: "Rejected" })
          : status === "ignored"
            ? t("review.ignored", { defaultValue: "Ignored" })
            : status === "flagged"
              ? t("review.flagged", { defaultValue: "Flagged for review" })
              : t("review.pending", { defaultValue: "Pending review" });
  const positive = status === "accepted" || status === "edited";
  return (
    <span
      className={clsx(
        "rounded-full px-2 py-0.5 text-[11px] font-medium",
        positive
          ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200"
          : status === "flagged"
            ? "bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200"
            : "bg-[rgb(var(--card-muted))] text-[rgb(var(--text-secondary))]",
      )}
    >
      {label}
    </span>
  );
}

/**
 * An answer or summary text laid out for reading: paragraphs kept apart, a
 * short lead-in ("Owed:", "Where it stands:") in bold, and "- " lines as
 * bullets. Plain text in, never HTML: nothing the model writes is rendered
 * as markup.
 */
export function AiText({ text, className }: { text: string; className?: string }) {
  const blocks = (text ?? "").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const lead = (line: string) => {
    const m = line.match(/^([A-Z][^:\n]{1,32}):\s+(.*)$/s);
    return m ? (
      <>
        <span className="font-semibold text-[rgb(var(--text-primary))]">{m[1]}:</span> {m[2]}
      </>
    ) : (
      line
    );
  };
  return (
    <div className={clsx("space-y-2.5 text-sm leading-relaxed text-[rgb(var(--text-primary))]", className)}>
      {blocks.map((block, i) => {
        const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        const bullets = lines.length > 1 && lines.every((l) => /^[-\u2022*]\s+/.test(l));
        if (bullets) {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5">
              {lines.map((l, j) => (
                <li key={j}>{lead(l.replace(/^[-\u2022*]\s+/, ""))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="whitespace-pre-line">
            {lead(block)}
          </p>
        );
      })}
    </div>
  );
}

/**
 * The summary's opening statement, set apart as the thing to read first,
 * with room beside it for a measure such as the "found" dial.
 */
export function SummaryOverview({
  statement,
  aside,
  onOpenSource,
}: {
  statement: AiSourcedStatement;
  aside?: React.ReactNode;
  onOpenSource?: (source: AiSource) => void;
}) {
  if (!statement?.text) return null;
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-[rgb(var(--text-accent))]/20 bg-[rgb(var(--text-accent))]/[0.05] p-3.5">
      <div className="min-w-[16rem] flex-1">
        <AiText text={statement.text} />
        <SourceList statement={statement} onOpenSource={onOpenSource} />
      </div>
      {aside}
    </div>
  );
}

/**
 * Summary fields as tiles. What the record or document does not state is
 * shown as a quiet dashed tile, so a gap never reads like an answer.
 */
export function SummaryFieldGrid({
  fields,
  onOpenSource,
}: {
  fields: { label: string; text: string; found: boolean; sources?: AiSource[]; sourceNotice?: string | null }[];
  onOpenSource?: (source: AiSource) => void;
}) {
  if (fields.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 gap-2 md:grid-cols-2">
      {fields.map((f) => (
        <div
          key={f.label}
          className={clsx(
            "flex min-w-0 flex-col rounded-xl border p-3",
            f.found
              ? "border-[rgb(var(--border))] bg-[rgb(var(--card-muted))]/40"
              : "border-dashed border-[rgb(var(--border))]",
          )}
        >
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-muted))]">{f.label}</dt>
          <dd
            className={clsx(
              "mt-1 text-sm leading-relaxed",
              f.found ? "text-[rgb(var(--text-primary))]" : "italic text-[rgb(var(--text-muted))]",
            )}
          >
            {f.text}
          </dd>
          {f.found && (
            <div className="mt-auto">
              <SourceList statement={{ sources: f.sources ?? [], sourceNotice: f.sourceNotice }} onOpenSource={onOpenSource} compact />
            </div>
          )}
        </div>
      ))}
    </dl>
  );
}

/** Numbered considerations (next steps, risks), each with its own sources. */
export function ConsiderList({
  title,
  items,
  note,
  tone = "accent",
  onOpenSource,
}: {
  title: string;
  items: AiSourcedStatement[];
  note?: string;
  tone?: "accent" | "warning";
  onOpenSource?: (source: AiSource) => void;
}) {
  if (items.length === 0) return null;
  const badge =
    tone === "warning"
      ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
      : "bg-[rgb(var(--text-accent))]/15 text-[rgb(var(--text-accent))]";
  return (
    <section className="space-y-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[rgb(var(--text-muted))]">{title}</h4>
      <ol className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3.5 py-2.5">
            <span className={clsx("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold", badge)}>
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <AiText text={item.text} />
              <SourceList statement={item} onOpenSource={onOpenSource} compact />
            </div>
          </li>
        ))}
      </ol>
      {note && <p className="text-[11px] text-[rgb(var(--text-muted))]">{note}</p>}
    </section>
  );
}
